import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { useEffect } from "react";

import { ErrorBanner } from "@/components/error-banner";
import { errorMessage, isApiError } from "@/lib/api/client";
import { useDocumentTitle, pageTitle } from "@/lib/document-title";

import { NotFound } from "./not-found";

/**
 * The per-route error boundary: a banner with Retry that resets the boundary, the failed
 * queries and the route's loader. A 404 from the API renders the not-found screen.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const queryReset = useQueryErrorResetBoundary();
  useEffect(() => {
    queryReset.reset();
  }, [queryReset]);
  useDocumentTitle(pageTitle("Something went wrong"));

  if (isApiError(error) && error.isNotFound) return <NotFound />;

  const retry = () => {
    queryReset.reset();
    reset();
    void router.invalidate();
  };

  return (
    <div className="content page-gutter py-8">
      <ErrorBanner message={errorMessage(error)} onRetry={retry} />
    </div>
  );
}
